import dayjs from 'dayjs';

import i18n from '@/core/i18n';
import { DEFAULT_COLOR_PALETTE } from '@/lib/constants/colors';
import { StringUtils } from '@/utils/string/string-utils';

import { AssignmentType } from '../types';
import { AssignmentResponse, PlannerAssignmentResponse } from '../types/dto';
import { PlannerAssignmentProjectResponse } from '../types/dto/response/planner-assignment-project-response';
import { AssignmentActivePeriodResponse } from '../types/dto/response/planner-assignment-response';
import { mapActivePeriodsFromDTO } from './assignment-active-period.mapper';
import { mapAssignmentStatusFromDTO } from './assignment-status.mapper';

export const mapAssignmentFromResponse = (
  response: AssignmentResponse,
  activePeriods?: AssignmentActivePeriodResponse[],
  isFilteredOut: boolean = false,
  belongsToTheGroup: boolean = true,
  adjustedMinutesByDate?: Record<string, number>,
  plannerProject?: PlannerAssignmentProjectResponse,
  bookedMinutes?: number
): AssignmentType => {
  const { minutesPerDay, startDate, endDate, status, project } = response;
  const hoursPerDay = minutesPerDay / 60;

  const color = project.colorHex ? '#' + project.colorHex : DEFAULT_COLOR_PALETTE.slate;
  const { periods, hasRateChanges } = mapActivePeriodsFromDTO(activePeriods, startDate, endDate);

  project.name = StringUtils.nullIfEmpty(project.name) ?? i18n.t('common:noNameProject');

  return {
    id: response.id,
    originalAssignment: response,
    plannerProject,
    title: project.name,
    startDate: dayjs(startDate),
    endDate: dayjs(endDate),
    color,
    hoursPerDay,
    status: mapAssignmentStatusFromDTO(status),
    probability: response.probability,
    activePeriods: periods,
    hasRateChanges,
    isFilteredOut,
    belongsToTheGroup,
    bookedMinutes,
    adjustedMinutesByDate,
  };
};

export const mapAssignmentsFromPlannerResponse = (responses: PlannerAssignmentResponse[]) =>
  responses.map(res =>
    mapAssignmentFromResponse(
      res.assignment,
      res.activePeriods,
      res.isFilteredOut,
      res.belongsToTheGroup,
      res.adjustedMinutesByDate,
      res.project,
      res.bookedMinutes ?? undefined
    )
  );

export const mapAssignmentsFromResponse = (responses: AssignmentResponse[]) =>
  responses.map(res => mapAssignmentFromResponse(res));
